import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { useParams, useHistory } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { sdk } from 'src/utilities/sdk';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { EnterpriseWizardCompletionModal } from 'src/components/EnterpriseWizardCompletionModal/EnterpriseWizardCompletionModal';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

import { EnterpriseCompanyDetailsStep } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseCompanyDetailsStep';
import { EnterpriseGroupsStep } from 'src/components/EnterpriseSignupSteps/EnterpriseGroupsStep/EnterpriseGroupsStep';
import { EnterpriseEmployeesStep } from 'src/components/EnterpriseSignupSteps/EnterpriseEmployeesStep/EnterpriseEmployeesStep';
import { ManageEmployeesStep } from 'src/components/enterprise/set-up/steps/ManageEmployeesStep';
import { EnterpriseInvoiceSettingsStep } from 'src/components/enterprise/set-up/steps/EnterpriseInvoiceSettingsStep/EnterpriseInvoiceSettingsStep';
import { EnterpriseBillingStep } from 'src/components/EnterpriseSignupSteps/EnterpriseBillingStep/EnterpriseBillingStep';

export interface IEnterpriseSignupStep {
  name: string;
  id: EnterpriseSignupStep;
  content?: JSX.Element;
}

export enum EnterpriseSignupStep {
  COMPANY_DETAILS = 'COMPANY_DETAILS',
  GROUPS = 'GROUPS',
  EMPLOYEES = 'EMPLOYEES',
  MANAGE_EMPLOYEES = 'MANAGE_EMPLOYEES',
  INVOICE_SETTINGS = 'INVOICE_SETTINGS',
  BILLING = 'BILLING',
}

export enum EnterpriseSignupStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum EnterpriseSignupStepStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export interface IEnterpriseSignupStatusResponse {
  date_created: string;
  date_updated: string;
  funnel_status: EnterpriseSignupStatus;
  funnel_steps: {
    [k in EnterpriseSignupStep]: EnterpriseSignupStepStatus;
  };
}

interface IEnterpriseSignupStatusContext {
  activeStep: EnterpriseSignupStep;
  currentStepNumber: number;
  isStepCompleted: (stepName: EnterpriseSignupStep) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  getStepNumber: (stepId: EnterpriseSignupStep) => number;
  corpId: string;
  getSteps: () => IEnterpriseSignupStep[];
  canManageCreditCard: boolean;
}

const SignupStatusContext = createContext<IEnterpriseSignupStatusContext>(null);

export const STEP_MAPPING = {
  1: EnterpriseSignupStep.COMPANY_DETAILS,
  2: EnterpriseSignupStep.GROUPS,
  3: EnterpriseSignupStep.EMPLOYEES,
  4: EnterpriseSignupStep.MANAGE_EMPLOYEES,
  5: EnterpriseSignupStep.INVOICE_SETTINGS,
  6: EnterpriseSignupStep.BILLING,
};

export const ENTERPRISE_STEP_CONTENT = {
  [EnterpriseSignupStep.COMPANY_DETAILS]: <EnterpriseCompanyDetailsStep />,
  [EnterpriseSignupStep.GROUPS]: <EnterpriseGroupsStep />,
  [EnterpriseSignupStep.EMPLOYEES]: <EnterpriseEmployeesStep />,
  [EnterpriseSignupStep.MANAGE_EMPLOYEES]: <ManageEmployeesStep />,
  [EnterpriseSignupStep.INVOICE_SETTINGS]: <EnterpriseInvoiceSettingsStep />,
  [EnterpriseSignupStep.BILLING]: <EnterpriseBillingStep />,
};

const SIGNUP_STEPS: IEnterpriseSignupStep[] = [
  {
    name: 'Company details',
    id: EnterpriseSignupStep.COMPANY_DETAILS,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.COMPANY_DETAILS],
  },
  {
    name: 'Groups',
    id: EnterpriseSignupStep.GROUPS,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.GROUPS],
  },
  {
    name: 'Employees',
    id: EnterpriseSignupStep.EMPLOYEES,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.EMPLOYEES],
  },
  {
    name: 'Manage employees',
    id: EnterpriseSignupStep.MANAGE_EMPLOYEES,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.MANAGE_EMPLOYEES],
  },
  {
    name: 'Invoice settings',
    id: EnterpriseSignupStep.INVOICE_SETTINGS,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.INVOICE_SETTINGS],
  },
  {
    name: 'Billing',
    id: EnterpriseSignupStep.BILLING,
    content: ENTERPRISE_STEP_CONTENT[EnterpriseSignupStep.BILLING],
  },
];

export function getEnterpriseSignupSteps(
  removeStepContent?: boolean
): IEnterpriseSignupStep[] {
  if (removeStepContent) {
    return SIGNUP_STEPS.map(({ name, id }) => {
      return {
        name,
        id,
      };
    });
  }

  return SIGNUP_STEPS;
}

interface IEnterpriseSignupStatus {
  ownerId?: string;
  dinerId?: string;
  step?: EnterpriseSignupStep;
  completedSteps?: EnterpriseSignupStep[];
  currentStepNumber?: number;
  currentStepStatus?: EnterpriseSignupStepStatus;
}
const DEFAULT_STEP_NUMBER = 1;

interface IEnterpriseSignupStatusProviderProps {
  children: ReactNode;
}

function EnterpriseSignupStatusProvider({
  children,
}: IEnterpriseSignupStatusProviderProps): JSX.Element {
  const { corpid, stepNumber: urlStepNumber } =
    useParams<{ corpid: string; stepNumber: string }>();
  const { push, replace } = useHistory();

  const {
    user,
    redirectToHomePage,
    corpData,
    enterpriseData: { isFinancialAdminUser, isParentAdminUser },
  } = useAuth();
  const { sendTealiumEvent } = useAnalytics();
  const [signupStatus, setSignupStatus] = useState<IEnterpriseSignupStatus>({});
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const canManageCreditCard = isFinancialAdminUser || isParentAdminUser;
  const shouldShowBillingPage =
    corpData && corpData.invoicePaymentMethod === 'CREDIT_CARD_PRIMARY';

  function getSteps(): IEnterpriseSignupStep[] {
    return getEnterpriseSignupSteps().filter(({ id }) => {
      // only the billing step is conditional
      if (id !== EnterpriseSignupStep.BILLING) {
        return true;
      }

      return shouldShowBillingPage;
    });
  }
  const NUMBER_OF_STEPS = getSteps().length;

  async function getEnterpriseSignupStatus(): Promise<void> {
    try {
      const statusResponse: IEnterpriseSignupStatusResponse =
        await sdk.http.get(
          `/corp-admin/corp/premium/${corpData.id}/onboarding-funnel`,
          {}
        );

      let stepNumber = validateStepNumber(Number(urlStepNumber));
      let completedSteps = [];

      if (statusResponse.funnel_status === EnterpriseSignupStatus.IN_PROGRESS) {
        // find the current step with the IN_PROGRESS status,
        // if there is more than one in the funnel_steps object, take the first step with the IN_PROGRESS status
        const currentStep = Object.keys(statusResponse.funnel_steps).find(
          (key) =>
            statusResponse.funnel_steps[key] ===
            EnterpriseSignupStepStatus.IN_PROGRESS
        );
        const currentUrlStep = STEP_MAPPING[stepNumber];

        if (
          currentStep !== currentUrlStep &&
          statusResponse.funnel_steps[currentUrlStep] !==
            EnterpriseSignupStepStatus.COMPLETED
        ) {
          stepNumber = findCurrentStepNumber(EnterpriseSignupStep[currentStep]);
          replace(`/corporate/enterprise/${corpid}/set-up/step/${stepNumber}`);
        }

        completedSteps = Object.keys(statusResponse.funnel_steps).reduce(
          (steps, step) =>
            statusResponse.funnel_steps[step] ===
            EnterpriseSignupStepStatus.COMPLETED
              ? [...steps, step]
              : steps,
          []
        );
      }

      if (statusResponse.funnel_status === EnterpriseSignupStatus.NOT_STARTED) {
        // change funnel status from NOT_STARTED to IN_PROGRESS for the first visit to the funnel
        await sdk.http.put(
          `/corp-admin/corp/premium/${corpData.id}/onboarding-funnel/status/${EnterpriseSignupStatus.IN_PROGRESS}`,
          {}
        );
        // change the first step status from NOT_STARTED to IN_PROGRESS for the first visit to the funnel
        await notifyStepStatus(DEFAULT_STEP_NUMBER);
        if (stepNumber !== DEFAULT_STEP_NUMBER) {
          replace(
            `/corporate/enterprise/${corpid}/set-up/step/${DEFAULT_STEP_NUMBER}`
          );
        }
      }

      if (statusResponse.funnel_status === EnterpriseSignupStatus.COMPLETED) {
        redirectToHomePage(corpData.id);
      }

      setSignupStatus({
        dinerId: user.ud_id,
        step: STEP_MAPPING[stepNumber],
        completedSteps,
        currentStepNumber: stepNumber,
        currentStepStatus:
          statusResponse.funnel_steps[STEP_MAPPING[stepNumber]],
      });
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function findCurrentStepNumber(currentStep: EnterpriseSignupStep): number {
    for (let i = 1; i <= NUMBER_OF_STEPS; i += 1) {
      if (currentStep === STEP_MAPPING[i]) {
        return i;
      }
    }

    return DEFAULT_STEP_NUMBER;
  }

  function validateStepNumber(stepNumber: number): number {
    if (stepNumber >= 1 && stepNumber <= NUMBER_OF_STEPS) {
      return stepNumber;
    }

    // default step will be step 1 if something invalid is given
    // TODO should this be an error state or redirect elsewhere?
    return DEFAULT_STEP_NUMBER;
  }

  async function goToNextStep(): Promise<void> {
    const { currentStepNumber } = signupStatus;

    const nextStepNumber = currentStepNumber + 1;

    if (nextStepNumber <= NUMBER_OF_STEPS) {
      // to prevent server errors, it only needs to notify the server about steps with the NOT_STARTED or IN_PROGRESS statuses
      if (
        signupStatus.currentStepStatus ===
          EnterpriseSignupStepStatus.IN_PROGRESS ||
        signupStatus.currentStepStatus ===
          EnterpriseSignupStepStatus.NOT_STARTED
      ) {
        try {
          await notifyStepStatus(nextStepNumber);
          push(`/corporate/enterprise/${corpid}/set-up/step/${nextStepNumber}`);
        } catch (error) {
          pushErrorFromAPI(error);
        }
      } else {
        push(`/corporate/enterprise/${corpid}/set-up/step/${nextStepNumber}`);
      }
    }

    if (currentStepNumber === NUMBER_OF_STEPS) {
      // change funnel status from IN_PROGRESS to COMPLETED
      try {
        await sdk.http.put(
          `/corp-admin/corp/premium/${corpData.id}/onboarding-funnel/status/${EnterpriseSignupStatus.COMPLETED}`,
          {}
        );
        sendFinishCTAAnalytics('success');
        setShowCompletionModal(true);
      } catch (error) {
        pushErrorFromAPI(error);
        sendFinishCTAAnalytics('error');
      }
    }
  }

  function sendFinishCTAAnalytics(eventlabel: 'success' | 'error'): void {
    sendTealiumEvent({
      eventcategory: getFinishCTAAnalyticsEventCategory(),
      eventaction: 'finish setup_cta',
      eventlabel,
    });
  }

  function getFinishCTAAnalyticsEventCategory(): string {
    switch (STEP_MAPPING[NUMBER_OF_STEPS]) {
      case EnterpriseSignupStep.BILLING:
        return 'corporate admin signup_billing';
      case EnterpriseSignupStep.INVOICE_SETTINGS:
        return 'corporate admin signup_invoice settings';
      default:
        return '';
    }
  }

  async function notifyStepStatus(currentStepNumber: number): Promise<void> {
    await sdk.http.put(
      `/corp-admin/corp/premium/${corpData.id}/onboarding-funnel/current-step/${STEP_MAPPING[currentStepNumber]}`,
      {}
    );
  }

  function goToPreviousStep(): void {
    const { currentStepNumber } = signupStatus;

    const previousStepNumber = currentStepNumber - 1;

    if (previousStepNumber >= 1) {
      sendTealiumEvent({
        eventcategory: 'corporate admin signup_all pages',
        eventaction: 'back_cta',
      });
      push(`/corporate/enterprise/${corpid}/set-up/step/${previousStepNumber}`);
    }
  }

  function isStepCompleted(stepId: EnterpriseSignupStep): boolean {
    const completedSteps = get(signupStatus, 'completedSteps') || [];

    return completedSteps.some((step) => step === stepId);
  }

  useEffect(() => {
    if (get(user, 'ud_id') && urlStepNumber && corpid && corpData) {
      getEnterpriseSignupStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, urlStepNumber, corpid, corpData]);

  if (!signupStatus.currentStepNumber) {
    return null;
  }

  return (
    <SignupStatusContext.Provider
      value={{
        activeStep: signupStatus.step,
        isStepCompleted,
        goToNextStep,
        goToPreviousStep,
        getStepNumber: findCurrentStepNumber,
        currentStepNumber: signupStatus.currentStepNumber,
        corpId: corpid as string,
        getSteps,
        canManageCreditCard,
      }}
    >
      {children}
      {showCompletionModal && (
        <EnterpriseWizardCompletionModal
          corpId={corpid as string}
          onClose={() => setShowCompletionModal(false)}
        />
      )}
    </SignupStatusContext.Provider>
  );
}

const useEnterpriseSignupStatus = (): IEnterpriseSignupStatusContext => {
  const context = useContext(SignupStatusContext);

  if (!context) {
    throw new Error(
      'useEnterpriseSignupStatus must be used within a EnterpriseSignupStatusProvider'
    );
  }
  return context;
};

export { EnterpriseSignupStatusProvider, useEnterpriseSignupStatus };
